import {BottomTabBarProps} from '@react-navigation/bottom-tabs';
import {createNativeStackNavigator} from '@react-navigation/native-stack';
import React, {useMemo, useState} from 'react';

import {useActiveUser} from '@/hooks/useActiveUser';
import {useResponsive} from '@/hooks/useResponsive';
import DropOnSpinampScreen from '@/modules/DropOnSpinamp/DropOnSpinampScreen';
import Notifications from '@/modules/Notifications/screens/Notifications';
import NotificationSettings from '@/modules/Notifications/screens/NotificationSettings/NotificationSettings';
import {TipsSettingsScreen} from '@/modules/Tip';
import {
  WalletScreen,
  WalletListScreen,
  WalletsSettingsScreen,
} from '@/modules/Wallets';
import {TabsContext} from '@/navigation/TabsNavigation/TabsContext';
import TabsNavigation from '@/navigation/TabsNavigation/TabsNavigation';
import TabsNavigationBar from '@/navigation/TabsNavigation/TabsNavigationBar';
import ActivityFeed from '@/screens/ActivityFeed/ActivityFeed';
import ArtistScreen from '@/screens/Artist/ArtistScreen';
import ArtistEdit from '@/screens/ArtistEdit/ArtistEdit';
import ArtistThemeEdit from '@/screens/ArtistThemeEdit/ArtistThemeEdit';
import Collector from '@/screens/Collector/Collector';
import CollectorEdit from '@/screens/CollectorEdit/CollectorEdit';
import CollectorThemeEdit from '@/screens/CollectorThemeEdit/CollectorThemeEdit';
import Genre from '@/screens/Genre';
import Platform from '@/screens/Platform/Platform';
import Playlist from '@/screens/Playlist/Playlist';
import PlaylistCurators from '@/screens/PlaylistCurators/PlaylistCurators';
import ImpersonatingReleasesForYou from '@/screens/ReleasesForYou/ImpersonatingReleasesForYouScreen';
import ReleasesForYou from '@/screens/ReleasesForYou/ReleasesForYouScreen';
import ThemesSettings from '@/screens/ThemesSettings/ThemesSettings';
import TrackScreen from '@/screens/Track/TrackScreen';
import {MainStackParams, Routes} from '@/types/routes';

const Stack = createNativeStackNavigator<MainStackParams>();

const MainNavigation = () => {
  const {isMobile} = useResponsive();
  const activeUser = useActiveUser();
  const [tabsContext, setTabsContext] = useState<Omit<
    BottomTabBarProps,
    'insets'
  > | null>(null);
  const contextValue = useMemo(() => ({setTabsContext}), [setTabsContext]);

  return (
    <TabsContext.Provider value={contextValue}>
      <Stack.Navigator screenOptions={{headerShown: false}}>
        <Stack.Screen name={Routes.Tabs} component={TabsNavigation} />
        <Stack.Screen name={Routes.ReleasesForYou} component={ReleasesForYou} />
        <Stack.Screen
          name={Routes.ImpersonatingReleasesForYou}
          component={ImpersonatingReleasesForYou}
        />
        <Stack.Screen name={Routes.Artist} component={ArtistScreen} />
        <Stack.Screen name={Routes.Track} component={TrackScreen} />
        <Stack.Screen name={Routes.Collector} component={Collector} />
        {/* Fallback for legacy route */}
        <Stack.Screen name={Routes.Collection} component={Collector} />
        <Stack.Screen name={Routes.Playlist} component={Playlist} />
        <Stack.Screen
          name={Routes.PlaylistCurators}
          component={PlaylistCurators}
        />
        <Stack.Screen name={Routes.Platform} component={Platform} />
        <Stack.Screen name={Routes.Genre} component={Genre} />
        <Stack.Screen name={Routes.ActivityFeed} component={ActivityFeed} />
        <Stack.Screen name={Routes.ThemesSettings} component={ThemesSettings} />
        <Stack.Screen name={Routes.Wallet} component={WalletScreen} />
        {activeUser && (
          <Stack.Screen name={Routes.Notifications} component={Notifications} />
        )}
        {activeUser && (
          <Stack.Screen
            name={Routes.NotificationSettings}
            component={NotificationSettings}
          />
        )}

        {activeUser && (
          <Stack.Group screenOptions={{gestureEnabled: false}}>
            <Stack.Screen name={Routes.ArtistEdit} component={ArtistEdit} />
            <Stack.Screen
              name={Routes.CollectorEdit}
              component={CollectorEdit}
            />
            <Stack.Screen
              name={Routes.CollectorThemeEdit}
              component={CollectorThemeEdit}
            />
            <Stack.Screen
              name={Routes.ArtistThemeEdit}
              component={ArtistThemeEdit}
            />
          </Stack.Group>
        )}
        {activeUser && (
          <Stack.Screen name={Routes.Wallets} component={WalletListScreen} />
        )}
        {activeUser && (
          <Stack.Screen
            name={Routes.WalletsSettings}
            component={WalletsSettingsScreen}
          />
        )}
        {activeUser?.artistProfile && (
          <Stack.Screen
            name={Routes.TipsSettings}
            component={TipsSettingsScreen}
          />
        )}
        <Stack.Screen
          name={Routes.DropOnSpinamp}
          component={DropOnSpinampScreen}
        />
      </Stack.Navigator>
      {isMobile && tabsContext && <TabsNavigationBar {...tabsContext} />}
    </TabsContext.Provider>
  );
};

export default MainNavigation;
